/**
 * The two charts in the empty space beside Project Approval's tabs — Jay, 2026-09-21: "Two bar
 * charts: (a) CEO's cumulative approved project count, Team 1 vs Team 2, and (b) Active vs
 * Completed projects, also split by team — so he can see how much is currently running without
 * leaving this screen."
 *
 * BOTH COUNT THE CEO'S OWN DECISIONS — Max, 2026-09-21: "(b) chỉ xét những project dưới sự duyệt
 * của Young thui". It was built over the whole approved portfolio first, and the note stays because
 * the result looks wrong until you know why: of the 53 Young signed, not one has reached Complete,
 * so (b) reads 0 completed for both teams. The finished work is on the 59 projects approved by
 * `system` during the migration, which is not a decision anybody made. The sub-line under each
 * chart still names what it counts, so nobody has to assume.
 *
 * COLOUR IS THE TEAM, in both charts. (a) is cut by team and (b) by state, so the one thing they
 * share is who the work belongs to; giving (b) its own two colours would teach two codes on one
 * screen for the same two teams. Measured with the palette validator rather than chosen by eye:
 *   light  #1D4ED8 / #B45309 — ΔE 30.0 protan · 26.8 tritan · 35.3 normal, both ≥ 3:1 on surface
 *   dark   #2563EB / #D97706 — ΔE 32.3 protan · 29.3 tritan · 38.3 normal
 * The dark pair is its own step off the same two ramps, not the light pair lightened: #1D4ED8 on
 * the dark surface measures 2.6:1 and fails the contrast check.
 *
 * COLOUR IS NEVER THE ONLY THING SAYING WHICH: every bar carries its figure, the axis names the
 * group, and the legend names the teams. In print, in forced colours and for a reader who sees
 * neither hue, the chart still reads.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

/* One list, both charts. A team's colour cannot differ between them, which is the whole reason the
   colours live here and not beside each chart. */
const TEAM_COLORS = ['var(--pac-a)', 'var(--pac-b)'];

const shortTeam = (name) => String(name || '').replace(/^Sales\s*OP\s+/i, '').trim() || '—';

/** A group of bars sharing one scale. `bars` is [{ key, label, value, color, ids }].
    A BAR IS A BUTTON — Max, 2026-10-20: "khi click vào thì phía dưới sẽ hiện danh sách project tương
    ứng nhé". Pressing one hands its projects to the screen, which lists them in the table below;
    pressing the lit one again hands nothing back. A bar at zero has nothing to list and is dead. */
function BarGroup({ bars, max, height = 92, pick, onPick }) {
  const { t } = useTranslation();
  return (
    <div className="pac-group">
      {bars.map((b) => {
        const h = max > 0 ? Math.round((b.value / max) * height) : 0;
        const on = pick === b.key;
        return (
          <button
            type="button"
            className={`pac-bar${on ? ' on' : ''}${pick && !on ? ' dim' : ''}`}
            key={b.key}
            title={b.value ? t('xCharts.approval.barTitle', { label: b.label, n: b.value }) : `${b.label}: 0`}
            aria-pressed={on}
            disabled={!onPick || !b.value}
            onClick={() => onPick(on ? null : { key: b.key, label: b.label, ids: b.ids || [] })}
          >
            {/* The figure, always — a bar 3px tall is unreadable and a bar at zero is invisible. */}
            <span className="pac-val">{b.value}</span>
            <span
              className="pac-fill"
              /* 3px even at zero, so an empty category is a mark that is present and empty rather
                 than a gap the eye reads as a missing category. */
              style={{ height: `${Math.max(h, b.value > 0 ? 4 : 3)}px`, background: b.color }}
            />
          </button>
        );
      })}
    </div>
  );
}

export default function ApprovalCharts({ charts, approverName, pick, onPick }) {
  const { t } = useTranslation();
  const teams = charts?.teams || [];
  if (teams.length < 1) return null;
  const colorOf = (team) => TEAM_COLORS[teams.indexOf(team) % TEAM_COLORS.length];

  const approved = charts.approved || [];
  const running = charts.running || [];
  const maxA = Math.max(1, ...approved.map((x) => x.n));
  const maxB = Math.max(1, ...running.flatMap((x) => [x.active, x.done, x.dropped || 0]));

  return (
    <div className="pac-wrap">
      <figure className="pac-card">
        <figcaption>
          {t('xCharts.approval.approvedBy', { name: approverName || t('xCharts.approval.theCeo') })}
          <em>{t('xCharts.approval.ownDecisions', { n: approved.reduce((a, x) => a + x.n, 0) })}</em>
        </figcaption>
        {/* One series, so no legend box: the axis under each bar names it. */}
        <BarGroup
          max={maxA}
          pick={pick}
          onPick={onPick}
          bars={approved.map((x) => ({
            key: `approved-${x.team}`, label: t('xCharts.approval.teamApproved', { team: shortTeam(x.team) }), value: x.n,
            color: colorOf(x.team), ids: x.ids,
          }))}
        />
        <div className="pac-axis">
          {approved.map((x) => <span key={x.team}>{shortTeam(x.team)}</span>)}
        </div>
      </figure>

      <figure className="pac-card">
        <figcaption>
          {t('xCharts.approval.runningTitle')}
          <em>{t('xCharts.approval.ofWhatHeApproved', { n: running.reduce((a, x) => a + x.active + x.done + x.dropped, 0) })}</em>
        </figcaption>
        <div className="pac-pairs">
          {[['active', t('xCharts.approval.running')], ['done', t('xCharts.approval.completed')], ['dropped', t('xCharts.approval.dropped')]].map(([key, label]) => (
            <div className="pac-pair" key={key}>
              <BarGroup
                max={maxB}
                pick={pick}
                onPick={onPick}
                bars={running.map((x) => ({
                  key: `${key}-${x.team}`, label: t('xCharts.approval.teamState', { team: shortTeam(x.team), state: label.toLocaleLowerCase() }),
                  value: x[key] || 0, color: colorOf(x.team), ids: x.ids?.[key],
                }))}
              />
              <div className="pac-axis one"><span>{label}</span></div>
            </div>
          ))}
        </div>
        {/* Two series, so the legend is always there — identity is never colour alone. */}
        <div className="pac-legend">
          {teams.map((team) => (
            <span key={team}><i style={{ background: colorOf(team) }} />{shortTeam(team)}</span>
          ))}
        </div>
        {/* Dropped was a footnote here ("n dropped, counted in neither") until 2026-10-20; it is a
            column of its own now, never folded into Running. */}
      </figure>
    </div>
  );
}
